import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { TRANSFORMERS } from '@lexical/markdown';
import {
  InitialConfigType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { makeStyles } from '@material-ui/styles';
import cn from 'classnames';
import {
  $createParagraphNode,
  $getRoot,
  ElementNode,
  createEditor,
} from 'lexical';
import { MentionNode } from './nodes/mention-node';
import AutoLinkPlugin from './plugins/auto-link-plugin';
import CodeHighlightPlugin from './plugins/code-highlight-plugin';
import ListMaxIndentLevelPlugin from './plugins/list-max-indent-level-plugin';
import MentionPlugin, { MentionSource } from './plugins/mention-plugin';
import OnChangeHTMLPlugin, {
  normalizeHTML,
} from './plugins/on-change-html-plugin';
import ToolbarPlugin from './plugins/toolbar-plugin';
import ExampleTheme from './theme';

import { useState, useEffect } from 'react';

import { COLORS } from '../../theme/variables';
import { length } from '../../utils/wysiwyg';

const useStyles = makeStyles({
  editorContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
    fontWeight: 400,
    textAlign: 'left',
    borderRadius: '3px',
    border: '1px solid #BEC6D3',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  error: {
    borderColor: `${COLORS.COLOR_RED_BASE} !important`,
  },
  editorInner: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    width: '100%',
    background: '#fff',
    position: 'relative',
    overflow: 'hidden',

    '& .editor-input': {
      flexGrow: 1,
      minHeight: '150px',
      height: '100%',
      overflowY: 'auto',
      overflowX: 'hidden',
      resize: 'none',
      fontSize: '16px',
      caretColor: 'rgb(5, 5, 5)',
      position: 'relative',
      tabSize: 1,
      outline: 0,
      padding: '15px 15px',
    },

    '& .editor-placeholder': {
      color: '#999',
      overflow: 'hidden',
      position: 'absolute',
      textOverflow: 'ellipsis',
      top: '15px',
      left: '15px',
      fontSize: '16px',
      userSelect: 'none',
      display: 'inline-block',
      pointerEvents: 'none',
    },

    '& .editor-text-bold': {
      fontWeight: 'bold',
    },

    '& .editor-text-italic': {
      fontStyle: 'italic',
    },

    '& .editor-text-underline': {
      textDecoration: 'underline',
    },

    '& .editor-text-strikethrough': {
      textDecoration: 'line-through',
    },

    '& .editor-text-underlineStrikethrough': {
      textDecoration: 'underline line-through',
    },

    '& .editor-text-code': {
      backgroundColor: 'rgb(240, 242, 245)',
      padding: '1px 0.25rem',
      fontFamily: 'Menlo, Consolas, Monaco, monospace',
      fontSize: '94%',
    },

    '& .editor-link': {
      color: 'rgb(33, 111, 219)',
      textDecoration: 'none',
    },

    '& .editor-code': {
      backgroundColor: 'rgb(240, 242, 245)',
      fontFamily: 'Menlo, Consolas, Monaco, monospace',
      display: 'block',
      padding: '8px 8px 8px 52px',
      lineHeight: '1.53',
      fontSize: '13px',
      margin: 0,
      marginTop: '8px',
      marginBottom: '8px',
      tabSize: 2,
      overflowX: 'auto',
      position: 'relative',

      '&:before': {
        content: 'attr(data-gutter)',
        position: 'absolute',
        backgroundColor: '#eee',
        left: 0,
        top: 0,
        borderRight: '1px solid #ccc',
        padding: '8px',
        color: '#777',
        whiteSpace: 'pre-wrap',
        textAlign: 'right',
        minWidth: '25px',
      },
      '&:after': {
        content: 'attr(data-highlight-language)',
        top: 0,
        right: '3px',
        padding: '3px',
        fontSize: '10px',
        textTransform: 'uppercase',
        position: 'absolute',
        color: 'rgba(0, 0, 0, 0.5)',
      },
    },

    '& .editor-tokenComment': {
      color: 'slategray',
    },

    '& .editor-tokenPunctuation': {
      color: '#999',
    },

    '& .editor-tokenProperty': {
      color: '#905',
    },

    '& .editor-tokenSelector': {
      color: '#690',
    },

    '& .editor-tokenOperator': {
      color: '#9a6e3a',
    },

    '& .editor-tokenAttr': {
      color: '#07a',
    },

    '& .editor-tokenVariable': {
      color: '#e90',
    },

    '& .editor-tokenFunction': {
      color: '#dd4a68',
    },

    '& .editor-paragraph': {
      margin: 0,
      marginBottom: '8px',
      position: 'relative',
    },

    '& .editor-paragraph:last-child': {
      marginBottom: 0,
    },

    '& .editor-heading-h1': {
      fontSize: '24px',
      color: 'rgb(5, 5, 5)',
      fontWeight: 400,
      margin: 0,
      marginBottom: '12px',
      padding: 0,
    },

    '& .editor-heading-h2': {
      fontSize: '16px',
      color: 'rgb(101, 103, 107)',
      fontWeight: 700,
      margin: 0,
      marginTop: '10px',
      padding: 0,
      textTransform: 'uppercase',
    },

    '& .editor-quote': {
      margin: 0,
      marginLeft: '20px',
      fontSize: '16px',
      color: 'rgb(101, 103, 107)',
      borderLeftColor: 'rgb(206, 208, 212)',
      borderLeftWidth: '4px',
      borderLeftStyle: 'solid',
      paddingLeft: '16px',
    },

    '& .editor-list-ol': {
      padding: 0,
      margin: 0,
      marginLeft: '16px',
      listStyleType: 'decimal',
      paddingLeft: '20px',
    },

    '& .editor-list-ul': {
      padding: 0,
      margin: 0,
      marginLeft: '16px',
      listStyleType: 'disc',
      paddingLeft: '20px',
    },

    '& .editor-listitem': {
      margin: '8px 0',
      display: 'list-item',
    },

    '& .editor-nested-listitem': {
      listStyleType: 'none',
    },

    '& pre::-webkit-scrollbar': {
      background: 'transparent',
      width: '10px',
    },

    '& pre::-webkit-scrollbar-thumb': {
      background: '#999',
    },

    '& .debug-timetravel-panel': {
      overflow: 'hidden',
      padding: '0 0 10px 0',
      margin: 'auto',
      display: 'flex',
    },

    '& .debug-timetravel-panel-slider': {
      padding: 0,
      flex: 8,
    },

    '& .debug-timetravel-panel-button': {
      padding: 0,
      border: 0,
      background: 'none',
      flex: 1,
      color: '#fff',
      fontSize: '12px',
    },

    '& .debug-timetravel-panel-button:hover': {
      textDecoration: 'underline',
    },

    '& .debug-timetravel-button': {
      border: 0,
      padding: 0,
      fontSize: '12px',
      top: '10px',
      right: '15px',
      position: 'absolute',
      background: 'none',
      color: '#fff',
    },

    '& .debug-timetravel-button:hover': {
      textDecoration: 'underline',
    },
  },
  count: {
    position: 'absolute',
    right: '15px',
    bottom: '5px',
    fontSize: '10px',
    background: '#fff',
    padding: '2px 4px',
    borderRadius: '3px',
    minWidth: '15px',
    textAlign: 'center',
    color: '#555',
    fontWeight: 'bolder',
    border: '1px solid rgb(221, 221, 221)',
    cursor: 'default',
    transition: 'opacity .3s',
    zIndex: 10,
  },
});

// Function to create EditorState JSON from HTML string
function createEditorStateFromHTML(htmlString: string): string {
  // If no HTML string provided, return a basic empty state
  if (!htmlString || htmlString.trim() === '') {
    return JSON.stringify({
      root: {
        children: [
          {
            children: [],
            direction: null,
            format: '',
            indent: 0,
            type: 'paragraph',
            version: 1,
          },
        ],
        direction: null,
        format: '',
        indent: 0,
        type: 'root',
        version: 1,
      },
    });
  }

  // Create a temporary editor to parse the HTML
  const tempEditor = createEditor({
    namespace: 'temp',
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      MentionNode,
    ],
    onError: () => { },
  });

  try {
    tempEditor.update(
      () => {
        const root = $getRoot();
        root.clear();

        // Parse HTML string into DOM and convert to Lexical nodes
        const parser = new DOMParser();
        const dom = parser.parseFromString(htmlString, 'text/html');
        const nodes = $generateNodesFromDOM(tempEditor, dom);

        // Check if we have any block-level elements
        const hasBlockElements = nodes.some(
          (node) =>
            node.getType() === 'paragraph' ||
            node.getType() === 'heading' ||
            node.getType() === 'list' ||
            node.getType() === 'quote' ||
            node.getType() === 'code' ||
            node.getType() === 'table',
        );

        if (hasBlockElements) {
          // If we have block elements, append them directly
          nodes.forEach((node) => {
            const nodeType = node.getType();

            if (
              nodeType === 'paragraph' ||
              nodeType === 'heading' ||
              nodeType === 'list' ||
              nodeType === 'quote' ||
              nodeType === 'code' ||
              nodeType === 'table'
            ) {
              root.append(node);
            } else {
              // For inline elements, try to append to the last block element
              const rootChildren = root.getChildren();
              if (rootChildren.length > 0) {
                const lastChild = rootChildren[rootChildren.length - 1];
                if (lastChild instanceof ElementNode) {
                  lastChild.append(node);
                } else {
                  // Wrap in paragraph as fallback
                  const paragraph = $createParagraphNode();
                  paragraph.append(node);
                  root.append(paragraph);
                }
              } else {
                // Wrap in paragraph as fallback
                const paragraph = $createParagraphNode();
                paragraph.append(node);
                root.append(paragraph);
              }
            }
          });
        } else {
          // If no block elements, wrap everything in a single paragraph
          const paragraph = $createParagraphNode();
          nodes.forEach((node) => {
            paragraph.append(node);
          });
          root.append(paragraph);
        }

        // Ensure we always have at least one paragraph
        if (root.getChildrenSize() === 0) {
          const paragraph = $createParagraphNode();
          root.append(paragraph);
        }
      },
      { discrete: true },
    );

    // Get the editor state after the update
    const editorStateJSON = JSON.stringify(
      tempEditor.getEditorState().toJSON(),
    );

    return editorStateJSON;
  } catch (error) {
    console.error('Error creating editor state from HTML:', error);
    // Return a basic empty state on error
    return JSON.stringify({
      root: {
        children: [
          {
            children: [],
            direction: null,
            format: '',
            indent: 0,
            type: 'paragraph',
            version: 1,
          },
        ],
        direction: null,
        format: '',
        indent: 0,
        type: 'root',
        version: 1,
      },
    });
  }
}

function CharacterCountPlugin({
  maxLength,
  className,
}: {
  maxLength?: number;
  className?: string;
}): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [characterCount, setCharacterCount] = useState(0);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        const normalizedHtml = normalizeHTML(html);
        setCharacterCount(length(normalizedHtml));
      });
    });
  }, [editor]);

  return (
    <span className={className}>
      {characterCount}
      {maxLength ? `/${maxLength}` : ''}
    </span>
  );
}

interface EditorProps {
  testid?: string;
  initialValue?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  containerClassName?: string;
  toolbarClassName?: string;
  hasMentions?: boolean;
  mentionSource?: MentionSource | null;
  showCount?: boolean;
  maxLength?: number;
  error?: boolean;
}

export default function Editor({
  testid,
  initialValue,
  onChange,
  placeholder = 'Enter some rich text...',
  disabled = false,
  containerClassName,
  toolbarClassName,
  hasMentions = false,
  mentionSource,
  showCount,
  maxLength,
  error,
}: EditorProps = {}) {
  const classes = useStyles();

  const placeholderComponent = (
    <div className='editor-placeholder'>{placeholder}</div>
  );

  const editorConfig: InitialConfigType = {
    namespace: 'editor',
    theme: ExampleTheme,
    onError(error: any) {
      throw error;
    },
    editable: !disabled,
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      MentionNode,
    ],
    // Set initial editor state if initialValue is provided
    ...(initialValue
      ? {
        editorState: createEditorStateFromHTML(initialValue),
      }
      : {}),
  };

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div
        className={cn(classes.editorContainer, containerClassName, {
          [classes.error]: error,
        })}
        data-testid={testid}>
        <ToolbarPlugin disabled={disabled} className={toolbarClassName} />
        <div className={classes.editorInner}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className='editor-input'
                onMouseDown={(e) => {
                  try {
                    const target = e.target as HTMLElement | null;
                    const anchor = target?.closest(
                      'a',
                    ) as HTMLAnchorElement | null;
                    if (anchor && anchor.href) {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(anchor.href, '_blank', 'noopener');
                    }
                  } catch (error) {
                    console.error(error);
                  }
                }}
              />
            }
            placeholder={placeholderComponent}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          {/* <AutoFocusPlugin defaultSelection='rootEnd' /> */}
          <CodeHighlightPlugin />
          <ListPlugin />
          <LinkPlugin />
          <AutoLinkPlugin />
          <ListMaxIndentLevelPlugin maxDepth={7} />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          {hasMentions && <MentionPlugin source={mentionSource} />}
          {onChange && <OnChangeHTMLPlugin onChange={onChange} />}
          {showCount && (
            <CharacterCountPlugin
              maxLength={maxLength}
              className={classes.count}
            />
          )}
        </div>
      </div>
    </LexicalComposer>
  );
}
